import type { CardDetail } from "@seagames/contracts";
import { Modal, ModalActions, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { AccessCodePill } from "../../components/access-codes/access-code-pill";
import { AlertTriangleIcon, CheckIcon, ChevronRightIcon, PrinterIcon, UndoIcon } from "../../components/accreditation/icons";
import { JourneyTracker } from "../../components/accreditation/journey";
import { formatDateOnly } from "../../components/accreditation/model";
import { CategoryChip } from "../../components/accreditation/parts";
import { ActivateSheet, LoadError, PrintBatchSheet, type SheetTarget } from "../../components/e-accreditation/bulk-sheets";
import { CardVisual } from "../../components/e-accreditation/card-visual";
import { undoWindow } from "../../components/e-accreditation/model";
import { CardStateChip, CardStatesGuide } from "../../components/e-accreditation/parts";
import { ReportLostSheet } from "../../components/e-accreditation/report-lost-sheet";
import { usePageBar } from "../../components/shell/page-bar";
import { accessCodeOptionsOptions } from "../../lib/access-codes/queries";
import { ApiError } from "../../lib/api/client";
import { cardDetailOptions, invalidateEAccreditation, markPrintBatchPrinted, undoCardIncident } from "../../lib/api/e-accreditation";
import { gamesDetailOptions } from "../../lib/competition/games";
import { canPrintCards, canSeePrintBatches } from "../../lib/e-accreditation/access";
import { formatClock, formatDateTime, formatShort } from "../../lib/format/datetime";
import { toast } from "../../lib/ui/toast";

/**
 * One card (her `pgECard`, admin.js:1727): the e-card on the left with its
 * state, validity, activation and printing; on the right the actions, the
 * card's access and its holder. A lost report shows her Undo banner while
 * the API still allows the undo (CARD_INCIDENT_UNDO_MINUTES).
 *
 * The QR is drawn only when the API sends the issue's token (admins and
 * host groups that print or activate; never a NOC).
 *
 * The actions are exactly what `GET /cards/:id` says (`actions`) — the same
 * SQL the writes use — plus "Add to print batch" for a printer while the
 * card waits for one (the server skips it with a reason otherwise). Her
 * Upgrade and Replacement tiles, and the card history, are later slices.
 *
 * The print pipeline (0075): the card's stepper is the API's `journey` (the
 * host's every step, the NOC's folded one, or a self-printed card's
 * Download → Activated), and a card in a batch links to its batch — for a
 * NOC only its own self-print batch ("Download your card").
 */
export const Route = createFileRoute("/_app/accreditation/e-accreditation/$cardId")({
	component: CardPage,
});

function CardPage() {
	const { t } = useTranslation("eAccreditation");
	const { t: tA } = useTranslation("accreditation");
	const { cardId } = Route.useParams();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const { session } = Route.useRouteContext();
	const card = useQuery(cardDetailOptions(cardId));
	const codes = useQuery(accessCodeOptionsOptions());
	const games = useQuery({ ...gamesDetailOptions(card.data?.games_id ?? ""), enabled: !!card.data });
	const [activate, setActivate] = useState<SheetTarget | null>(null);
	const [print, setPrint] = useState<SheetTarget | null>(null);
	const [reportOpen, setReportOpen] = useState(false);
	const [markOpen, setMarkOpen] = useState(false);

	usePageBar({
		title: t("card.title"),
		subtitle: card.data ? (card.data.accreditation_no_display ?? card.data.registration_no_display) : undefined,
		back: { to: "/accreditation/e-accreditation/cards", search: { games_id: search.games_id }, label: t("card.back") },
	});

	if (card.isPending)
		return (
			<div className="grid gap-[1rem] min-[64rem]:grid-cols-[19rem_minmax(0,1fr)]">
				<div className="skeleton h-[30rem] rounded-[0.875rem]" />
				<div className="skeleton h-[24rem] rounded-[0.625rem]" />
			</div>
		);
	if (card.isError) {
		const notFound = card.error instanceof ApiError && (card.error.code === "NOT_FOUND" || card.error.code === "FORBIDDEN_SCOPE");
		return notFound ? (
			<div role="alert" className="grid justify-items-center gap-[0.5rem] rounded-[0.625rem] border border-gms-line bg-base-100 px-[1rem] py-[2.5rem] text-center">
				<b className="text-[0.9375rem] text-gms-navy-ink">{t("card.notFound")}</b>
			</div>
		) : (
			<LoadError error={card.error} onRetry={() => void card.refetch()} />
		);
	}

	const c = card.data;
	const canAddToBatch = canPrintCards(session) && c.card_state === "NOT_PRINTED" && c.print_batch === null;
	const one: SheetTarget = { kind: "card", row: c };

	const tiles: Array<{ key: string; icon: ReactNode; label: string; hint: string; tone: "navy" | "bad"; onClick: () => void }> = [];
	if (c.actions.activate) tiles.push({ key: "activate", icon: <CheckIcon />, label: t("card.actions.activate"), hint: t("card.actions.activateHint"), tone: "navy", onClick: () => setActivate(one) });
	if (canAddToBatch)
		tiles.push({ key: "print", icon: <PrinterIcon />, label: t("card.actions.addToBatch"), hint: t("card.actions.addToBatchHint"), tone: "navy", onClick: () => setPrint({ kind: "rows", rows: [c] }) });
	const batchId = c.issue?.print_batch_id ?? null;
	const batchName = c.issue?.print_batch_name ?? c.print_batch?.name ?? null;
	if (c.actions.mark_printed && batchId && batchName)
		tiles.push({
			key: "printed",
			icon: <PrinterIcon />,
			label: t("card.actions.markPrinted"),
			hint: t("card.actions.markPrintedHint", { name: batchName }),
			tone: "navy",
			onClick: () => setMarkOpen(true),
		});
	// A card in a live batch opens it: the host's printers follow the batch; a NOC downloads its own self-print batch.
	const liveBatch = c.issue && !c.issue.voided_at && batchId && batchName ? { id: batchId, name: batchName } : null;
	if (liveBatch && canSeePrintBatches(session)) {
		const download = c.issue?.print_batch_kind === "SELF_PRINT" && c.card_state === "READY_TO_GENERATE";
		tiles.push({
			key: "batch",
			icon: <PrinterIcon />,
			label: download ? t("card.actions.download") : t("card.actions.openBatch"),
			hint: download ? t("card.actions.downloadHint", { name: liveBatch.name }) : t("card.actions.openBatchHint", { name: liveBatch.name }),
			tone: "navy",
			onClick: () => void navigate({ to: "/accreditation/e-accreditation/print-batches", search: { games_id: search.games_id, batch: liveBatch.id } }),
		});
	}
	if (c.actions.report_incident)
		tiles.push({ key: "report", icon: <AlertTriangleIcon />, label: t("card.actions.report"), hint: t("card.actions.reportHint"), tone: "bad", onClick: () => setReportOpen(true) });

	return (
		<div className="grid gap-[1rem]">
			{c.actions.undo_incident && c.last_incident && <UndoBanner card={c} />}

			<div className="grid items-start gap-[1rem] min-[64rem]:grid-cols-[19rem_minmax(0,1fr)] min-[64rem]:gap-[1.25rem]">
				<div className="grid gap-[0.75rem] min-[64rem]:sticky min-[64rem]:top-[6.25rem]">
					<CardVisual card={c} codeOptions={codes.data} qrToken={c.issue?.qr_token ?? null} />
					<dl className="grid grid-cols-2 overflow-hidden rounded-[0.625rem] border border-gms-line bg-base-100">
						<Fact label={t("card.facts.state")}>
							<CardStateChip state={c.card_state} />
						</Fact>
						<Fact label={t("card.facts.validUntil")}>{c.valid_until ? formatDateOnly(c.valid_until) : "—"}</Fact>
						<Fact label={t("card.facts.activated")} sub={c.activated_by ? t("common.byName", { name: c.activated_by.name }) : undefined}>
							{c.activated_at ? (
								<time dateTime={c.activated_at} title={formatDateTime(c.activated_at)}>
									{formatShort(c.activated_at)}
								</time>
							) : (
								"—"
							)}
						</Fact>
						<Fact label={t("card.facts.printed")} sub={batchName ?? undefined}>
							{c.printed_at ? (
								<time dateTime={c.printed_at} title={formatDateTime(c.printed_at)}>
									{formatShort(c.printed_at)}
								</time>
							) : (
								t("common.notYet")
							)}
						</Fact>
					</dl>
				</div>

				<div className="grid min-w-0 gap-[1.125rem]">
					<section aria-labelledby="ecard-journey" className="grid min-w-0 gap-[0.5rem]">
						<h2 id="ecard-journey" className="px-[0.25rem] text-[0.875rem] font-bold text-gms-navy-ink">
							{tA("record.journey.title")}
						</h2>
						<div className="rounded-[0.625rem] border border-gms-line bg-base-100">
							<JourneyTracker status="APPROVED" journey={c.journey} validUntil={c.valid_until} />
						</div>
					</section>
					<section aria-labelledby="ecard-actions" className="grid gap-[0.5rem]">
						<div className="flex items-center justify-between gap-[0.5rem] px-[0.25rem]">
							<h2 id="ecard-actions" className="text-[0.875rem] font-bold text-gms-navy-ink">
								{t("card.actions.title")}
							</h2>
							<CardStatesGuide size="sm" />
						</div>
						{tiles.length === 0 ? (
							<p className="rounded-[0.625rem] border border-gms-line bg-base-100 px-[0.875rem] py-[0.75rem] text-[0.8125rem] text-gms-ink-3">{t("card.actions.none")}</p>
						) : (
							<div className="grid gap-[0.5rem] min-[36rem]:grid-cols-2 min-[80rem]:grid-cols-4">
								{tiles.map((x) => (
									<button
										key={x.key}
										type="button"
										onClick={x.onClick}
										className="grid cursor-pointer content-start justify-items-start gap-[0.375rem] rounded-[0.625rem] border border-gms-line bg-base-100 px-[0.875rem] py-[0.75rem] text-left transition-colors outline-none hover:border-gms-navy focus-visible:ring-2 focus-visible:ring-gms-navy/40"
									>
										<span
											className={`grid size-[2rem] place-items-center rounded-[0.5rem] [&>svg]:size-[1.0625rem] ${x.tone === "bad" ? "bg-gms-bad-soft text-gms-bad" : "bg-gms-navy-soft text-gms-navy"}`}
										>
											{x.icon}
										</span>
										<b className="text-[0.875rem] font-semibold text-gms-navy-ink">{x.label}</b>
										<span className="text-[0.75rem] leading-snug text-gms-ink-3">{x.hint}</span>
									</button>
								))}
							</div>
						)}
					</section>

					<Block id="ecard-access" title={t("card.access.title")}>
						<Row label={t("card.access.function")}>
							<span className="inline-flex items-center gap-[0.5rem]">
								<CategoryChip category={c.category} />
								{c.category.name}
							</span>
						</Row>
						<Row label={t("card.access.codes")}>
							{c.access.length === 0 ? (
								<span className="text-gms-ink-3">{t("card.access.none")}</span>
							) : (
								<ul className="flex flex-wrap gap-[0.375rem]">
									{c.access.map((a) => (
										<li key={a.access_code_id} className="inline-flex min-h-[1.875rem] items-center gap-[0.375rem] rounded-[0.5rem] bg-gms-soft py-[0.25rem] pr-[0.625rem] pl-[0.25rem] text-[0.78125rem] font-medium text-gms-ink-2">
											<AccessCodePill kind={a.kind} code={a.code} title={a.name} />
											{a.name}
										</li>
									))}
								</ul>
							)}
						</Row>
					</Block>

					<Block
						id="ecard-holder"
						title={t("card.holder.title")}
						end={
							<Link
								to="/accreditation/records/$id"
								params={{ id: c.ac_card_id }}
								className="inline-flex items-center gap-[0.125rem] text-[0.8125rem] font-semibold text-gms-navy hover:underline"
							>
								{t("card.holder.openRecord")}
								<ChevronRightIcon className="size-[0.875rem]" />
							</Link>
						}
					>
						<Row label={t("card.holder.name")}>
							<span className="font-semibold uppercase">{c.printed_name}</span>
						</Row>
						<Row label={t("card.holder.organisation")}>{c.organization.name}</Row>
						{c.sport && <Row label={t("card.holder.sport")}>{c.sport.name}</Row>}
						<Row label={t("card.holder.accreditationNo")}>
							<span className="tabular-nums">{c.accreditation_no_display ?? "—"}</span>
						</Row>
						<Row label={t("card.holder.registrationNo")}>
							<span className="tabular-nums">{c.registration_no_display}</span>
						</Row>
						{c.last_incident && !c.last_incident.undone_at && (
							<Row label={t("card.holder.lastIncident")}>
								<span className="grid">
									<span>
										{t("card.holder.incidentLine", { kind: t(`incidentKind.${c.last_incident.kind}`), date: formatDateOnly(c.last_incident.occurred_on) ?? "" })}
									</span>
									{c.last_incident.place && <span className="text-[0.75rem] text-gms-ink-3">{c.last_incident.place}</span>}
									{c.last_incident.police_report_no && (
										<span className="text-[0.75rem] text-gms-ink-3">{t("card.holder.police", { no: c.last_incident.police_report_no })}</span>
									)}
								</span>
							</Row>
						)}
					</Block>
				</div>
			</div>

			<ActivateSheet target={activate} gamesEndsOn={games.data?.ends_on} onClose={() => setActivate(null)} />
			<PrintBatchSheet target={print} onClose={() => setPrint(null)} />
			<ReportLostSheet card={c} open={reportOpen} onClose={() => setReportOpen(false)} />
			{batchId && batchName && <MarkPrintedModal open={markOpen} batchId={batchId} batchName={batchName} onClose={() => setMarkOpen(false)} />}
		</div>
	);
}

/** Her lost-report banner: reported at hh:mm, undo until hh:mm, "Undo report". Gone by itself when the window closes. */
function UndoBanner({ card }: { card: CardDetail }) {
	const { t } = useTranslation("eAccreditation");
	const incident = card.last_incident!;
	const [, tick] = useReducer((n: number) => n + 1, 0);
	const [busy, setBusy] = useState(false);
	const w = undoWindow(incident);
	useEffect(() => {
		if (!w.open) return;
		const id = window.setTimeout(tick, w.msLeft + 250);
		return () => window.clearTimeout(id);
	}, [w.open, w.msLeft]);
	if (!w.open) return null;

	const undo = async () => {
		setBusy(true);
		try {
			await undoCardIncident(card.ac_card_id, incident.card_incident_id);
			await invalidateEAccreditation();
			toast.success(t("undo.done"));
		} catch (err) {
			toast.error(err, t("undo.failed"));
		} finally {
			setBusy(false);
		}
	};

	return (
		<div role="status" className="flex flex-wrap items-center gap-x-[0.75rem] gap-y-[0.5rem] rounded-[0.625rem] border border-gms-bad/25 bg-gms-bad-soft px-[0.875rem] py-[0.625rem] text-[0.84375rem] text-gms-bad">
			<AlertTriangleIcon className="size-[1rem] shrink-0" />
			<span className="min-w-0 flex-1">
				{t("undo.banner", {
					kind: t(`incidentKindInline.${incident.kind}`),
					time: formatClock(new Date(incident.reported_at)),
					until: formatClock(new Date(incident.undo_until)),
				})}
			</span>
			<button type="button" onClick={() => void undo()} disabled={busy} className="btn btn-sm border-gms-bad/40 bg-base-100 text-gms-bad hover:border-gms-bad">
				{busy ? <span className="loading loading-spinner loading-xs" /> : <UndoIcon className="size-[0.9375rem]" />}
				{t("undo.button")}
			</button>
		</div>
	);
}

function MarkPrintedModal({ open, batchId, batchName, onClose }: { open: boolean; batchId: string; batchName: string; onClose: () => void }) {
	const { t } = useTranslation("eAccreditation");
	const { formatNumber: fmt } = useUiStrings();
	const [busy, setBusy] = useState(false);
	const run = async () => {
		setBusy(true);
		try {
			const res = await markPrintBatchPrinted(batchId);
			await invalidateEAccreditation();
			toast.success(t("batches.markDone", { count: res.moved, n: fmt(res.moved), name: batchName }));
			onClose();
		} catch (err) {
			toast.error(err, t("batches.markFailed"));
		} finally {
			setBusy(false);
		}
	};
	return (
		<Modal
			open={open}
			onClose={onClose}
			dismissible={!busy}
			size="sm"
			title={t("batches.markTitle", { name: batchName })}
			footer={
				<ModalActions>
					<button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>
						{t("common.cancel")}
					</button>
					<button type="button" className="btn btn-sm border-gms-navy bg-gms-navy text-white hover:bg-gms-navy-lift" disabled={busy} onClick={() => void run()}>
						{busy && <span className="loading loading-spinner loading-xs" />}
						{t("batches.markConfirm")}
					</button>
				</ModalActions>
			}
		>
			<p className="text-[0.84375rem] text-gms-ink-2">{t("card.markBody", { name: batchName })}</p>
		</Modal>
	);
}

function Fact({ label, sub, children }: { label: string; sub?: string; children: ReactNode }) {
	return (
		<div className="grid content-start gap-[0.25rem] px-[0.875rem] py-[0.625rem] shadow-[-1px_0_0_var(--color-gms-line),0_-1px_0_var(--color-gms-line)]">
			<dt className="text-[0.6875rem] font-semibold tracking-[0.04em] text-gms-ink-3 uppercase">{label}</dt>
			<dd className="text-[0.875rem] font-semibold text-gms-navy-ink tabular-nums">{children}</dd>
			{sub && <dd className="text-[0.75rem] text-gms-ink-3">{sub}</dd>}
		</div>
	);
}

function Block({ id, title, end, children }: { id: string; title: string; end?: ReactNode; children: ReactNode }) {
	return (
		<section aria-labelledby={id} className="grid gap-[0.5rem]">
			<div className="flex items-center justify-between gap-[0.5rem] px-[0.25rem]">
				<h2 id={id} className="text-[0.875rem] font-bold text-gms-navy-ink">
					{title}
				</h2>
				{end}
			</div>
			<dl className="rounded-[0.625rem] border border-gms-line bg-base-100 px-[1rem]">{children}</dl>
		</section>
	);
}

function Row({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div className="grid gap-[0.25rem] border-t border-gms-line py-[0.625rem] first:border-t-0 min-[36rem]:grid-cols-[minmax(8rem,40%)_minmax(0,1fr)] min-[36rem]:gap-[1rem]">
			<dt className="text-[0.8125rem] text-gms-ink-3">{label}</dt>
			<dd className="min-w-0 text-[0.875rem] text-gms-navy-ink">{children}</dd>
		</div>
	);
}
